/**
 * Deterministic local fixture data for profile/1D mode.
 *
 * FIXTURE BOUNDARY: This data is explicitly labelled as local fixture
 * data. It does NOT come from a backend profile service. When BE-122
 * (profile elevation API) ships, replace these arrays with real API
 * responses. Until then these fixtures allow the profile canvas to
 * render a working 1D view for development and testing.
 */

export interface ProfileStation {
  readonly id: string;
  readonly chainage: number;
  readonly elevation: number;
  readonly label: string;
}

export interface ProfileTrack {
  readonly id: string;
  readonly label: string;
  readonly kind: 'elevation' | 'depth' | 'sensor' | 'tolerance' | 'pick';
  readonly visible: boolean;
  readonly color: string;
  readonly data: ReadonlyArray<{ x: number; y: number }>;
}

export interface ProfileAnomaly {
  readonly id: string;
  readonly station: number;
  readonly reason: string;
  readonly confidence: 'high' | 'medium' | 'low';
  readonly delta: number;
  readonly unit: string;
}

export interface ProfileStats {
  readonly min: number;
  readonly max: number;
  readonly mean: number;
  readonly p10: number;
  readonly p50: number;
  readonly p90: number;
}

export interface ProfileFixture {
  readonly id: string;
  readonly label: string;
  readonly alignmentId: string;
  readonly axis: {
    readonly kind: 'elevation' | 'depth' | 'time' | 'sensor';
    readonly scale: 'linear' | 'log';
    readonly reversed: boolean;
    readonly units: string;
  };
  readonly stations: ReadonlyArray<ProfileStation>;
  readonly tracks: ReadonlyArray<ProfileTrack>;
  readonly anomalies: ReadonlyArray<ProfileAnomaly>;
  readonly stats: ProfileStats;
  readonly exaggeration: number;
}

function generateElevationData(count: number): Array<{ x: number; y: number }> {
  const data: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < count; i++) {
    const x = i * 25;
    const y = 120 + Math.sin(i * 0.3) * 40 + Math.cos(i * 0.07) * 20;
    data.push({ x, y: Math.round(y * 100) / 100 });
  }
  return data;
}

function generateToleranceData(count: number): Array<{ x: number; y: number }> {
  const data: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < count; i++) {
    const x = i * 25;
    const y = 115 + Math.sin(i * 0.3) * 35;
    data.push({ x, y: Math.round(y * 100) / 100 });
  }
  return data;
}

const STATION_COUNT = 32;

export const PROFILE_FIXTURE: ProfileFixture = {
  id: 'fixture-profile-001',
  label: 'Pipeline alignment A–B (fixture)',
  alignmentId: 'alignment-fixture-001',
  axis: {
    kind: 'elevation',
    scale: 'linear',
    reversed: false,
    units: 'm',
  },
  stations: Array.from({ length: STATION_COUNT }, (_, i) => ({
    id: `stn-${String(i).padStart(3, '0')}`,
    chainage: i * 25,
    elevation: 120 + Math.sin(i * 0.3) * 40 + Math.cos(i * 0.07) * 20,
    label: `STA ${i * 25}`,
  })),
  tracks: [
    {
      id: 'track-elevation',
      label: 'Ground elevation',
      kind: 'elevation',
      visible: true,
      color: 'var(--md-primary)',
      data: generateElevationData(STATION_COUNT),
    },
    {
      id: 'track-tolerance',
      label: 'Design tolerance',
      kind: 'tolerance',
      visible: true,
      color: 'var(--md-tertiary)',
      data: generateToleranceData(STATION_COUNT),
    },
  ],
  anomalies: [
    {
      id: 'anomaly-001',
      station: 200,
      reason: 'Elevation exceeds design tolerance',
      confidence: 'high',
      delta: 3.2,
      unit: 'm',
    },
    {
      id: 'anomaly-002',
      station: 475,
      reason: 'Sudden gradient change detected',
      confidence: 'medium',
      delta: 1.8,
      unit: 'm',
    },
  ],
  stats: {
    min: 82.4,
    max: 178.6,
    mean: 128.3,
    p10: 94.1,
    p50: 126.7,
    p90: 162.9,
  },
  exaggeration: 1,
};
